import { ThemeToggle } from "@components/ThemeToggle";
import { Badge } from "@components/ui/badge";
import { useAppDispatch } from "@hooks/useStore";
import { logout } from "@store/slices/auth";
import { cn } from "@utils/tailwindMerge";
import {
  // BookOpen,
  ChevronLeft,
  ChevronRight,
  Factory,
  Gift,
  LayoutDashboard,
  // Layers,
  LogOut,
  Megaphone,
  Package,
  // Settings,
  ShoppingBag,
  // Tag,
  Ticket,
  // Trophy,
  // Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import packageJson from "../../package.json";

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  path?: string;
}

interface NavSection {
  id: string;
  label?: string;
  items: NavItem[];
}

const mainNavItems: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: <LayoutDashboard className="w-6 h-6" />,
    path: "/dashboard",
  },
  {
    id: "plans",
    label: "Plans",
    icon: <Package className="w-6 h-6" />,
    path: "/plans",
  },
  {
    id: "coupons",
    label: "Coupons",
    icon: <Ticket className="w-6 h-6" />,
    path: "/coupons",
  },
  {
    id: "additional-items",
    label: "Additional Items",
    icon: <ShoppingBag className="w-6 h-6" />,
    path: "/additional-items",
  },
  // {
  //   id: "promotions",
  //   label: "Promotions",
  //   icon: <Tag className="w-6 h-6" />,
  //   path: "/promotions",
  // },
  {
    id: "campanhas",
    label: "Promoitems",
    icon: <Megaphone className="w-6 h-6" />,
    path: "/promoitems",
  },
  {
    id: "campaign-factory",
    label: "Campaign Factory",
    icon: <Factory className="w-6 h-6" />,
    path: "/campaign-factory",
  },
];

const brindesNavItems: NavItem[] = [
  {
    id: "promoitem-products",
    label: "Products",
    icon: <Gift className="w-6 h-6" />,
    path: "/promoitem-products",
  },
  // {
  //   id: "leituverso",
  //   label: "Leituverso",
  //   icon: <BookOpen className="w-6 h-6" />,
  //   path: "/leituverso",
  // },
];

// const partilharNavItems: NavItem[] = [
//   {
//     id: "ambassadors",
//     label: "Ambassadors",
//     icon: <Users className="w-6 h-6" />,
//     path: "/ambassadors",
//   },
//   {
//     id: "ambassador-configs",
//     label: "Ambassador Config",
//     icon: <Settings className="w-6 h-6" />,
//     path: "/ambassador-configs",
//   },
//   {
//     id: "prizes",
//     label: "Prizes",
//     icon: <Trophy className="w-6 h-6" />,
//     path: "/prizes",
//   },
//   {
//     id: "levels",
//     label: "Levels",
//     icon: <Layers className="w-6 h-6" />,
//     path: "/levels",
//   },
// ];

const bottomNavItems: NavItem[] = [
  // {
  //   id: "settings",
  //   label: "Configurações",
  //   icon: <Settings className="w-6 h-6" />,
  //   path: "/settings",
  // },
];

export function Navigation() {
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem("nav-expanded");
    return saved !== null ? saved === "true" : true;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const isSelected = (path?: string) => {
    if (!path) return false;
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  useEffect(() => {
    localStorage.setItem("nav-expanded", String(expanded));
  }, [expanded]);

  return (
    <aside
      className={cn(
        "bg-[#0D1415] flex flex-col transition-all duration-300 h-screen sticky top-0",
        expanded ? "w-[220px] min-w-[220px] max-w-[360px]" : "w-24"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center",
          expanded ? "py-7 px-5" : "py-7 px-5"
        )}
      >
        {expanded ? (
          <svg
            width="160"
            height="40"
            viewBox="0 0 160 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <text
              x="24"
              y="16"
              fontFamily="Inter, sans-serif"
              fontSize="16"
              fontWeight="700"
              fill="#DCE3E4"
              letterSpacing="-0.5"
            >
              Alpha
            </text>
            <text
              x="24"
              y="28"
              fontFamily="Inter, sans-serif"
              fontSize="9"
              fontWeight="500"
              fill="#88C0B2"
              letterSpacing="0.5"
            >
              CHECKOUTS PANEL
            </text>
          </svg>
        ) : (
          <svg
            width="60"
            height="40"
            viewBox="15 0 60 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <text
              x="24"
              y="16"
              fontFamily="Inter, sans-serif"
              fontSize="16"
              fontWeight="700"
              fill="#DCE3E4"
              letterSpacing="-0.5"
            >
              Alpha
            </text>
            <text
              x="24"
              y="28"
              fontFamily="Inter, sans-serif"
              fontSize="9"
              fontWeight="500"
              fill="#88C0B2"
              letterSpacing="0.5"
            >
              PANEL
            </text>
          </svg>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 w-full flex flex-col pt-11 overflow-y-auto">
        <div className="flex flex-col gap-1 mb-10">
          {([
            { id: "main", items: mainNavItems },
            { id: "brindes", label: "Gifts", items: brindesNavItems },
            // { id: "partilhar", label: "Share", items: partilharNavItems },
          ] as NavSection[]).map((section) => (
            <div key={section.id}>
              {section.label && (
                <div
                  className={cn(
                    "flex items-center gap-2 mt-4 mb-1",
                    expanded ? "mx-9" : "justify-center mx-2"
                  )}
                >
                  {expanded ? (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-[#DCE3E460] whitespace-nowrap">
                      {section.label}
                    </span>
                  ) : (
                    <div className="w-6 border-t border-[#DCE3E430]" />
                  )}
                </div>
              )}
              {section.items.map((item) => (
                <button
                  key={item.id}
                  className={cn(
                    "relative flex items-center gap-3 rounded-full transition-colors",
                    expanded ? "px-4 py-4 mx-5" : "h-16 flex-col justify-center",
                    isSelected(item.path) && "bg-[#135045]"
                  )}
                  onClick={() => item.path && navigate(item.path)}
                >
                  {expanded ? (
                    <>
                      <div
                        className={cn(
                          "flex-shrink-0",
                          isSelected(item.path)
                            ? "text-[#88C0B2]"
                            : "text-[#DCE3E4]"
                        )}
                      >
                        {item.icon}
                      </div>
                      <span
                        className={cn(
                          "text-sm font-medium whitespace-nowrap",
                          isSelected(item.path)
                            ? "text-[#88C0B2]"
                            : "text-[#DCE3E4]"
                        )}
                      >
                        {item.label}
                      </span>
                      {item.badge !== undefined && (
                        <Badge className="ml-auto h-[6px] w-[6px] min-w-[6px] p-0 rounded-full bg-[#BA1A1A] border-0" />
                      )}
                    </>
                  ) : (
                    <>
                      <div className="relative flex items-center justify-center w-14 h-14">
                        <div
                          className={cn(
                            "relative",
                            isSelected(item.path)
                              ? "text-[#88C0B2]"
                              : "text-[#DCE3E4]"
                          )}
                        >
                          {item.icon}
                        </div>
                        {item.badge !== undefined && (
                          <Badge className="absolute top-0 right-0 h-[6px] w-[6px] min-w-[6px] p-0 rounded-full bg-[#BA1A1A] border-0" />
                        )}
                      </div>
                    </>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Bottom Navigation */}
        <div className="flex-1 flex flex-col justify-end pb-5">
          <div className={cn("flex flex-col gap-1", expanded ? "px-5" : "")}>
            {bottomNavItems.map((item) => (
              <button
                key={item.id}
                className={cn(
                  "relative flex items-center gap-3 rounded-full transition-colors hover:bg-[#1A2425]",
                  expanded ? "px-4 py-4" : "h-16 flex-col justify-center",
                  isSelected(item.path) && "bg-[#135045]"
                )}
                onClick={() => item.path && navigate(item.path)}
              >
                {expanded ? (
                  <>
                    <div className="flex-shrink-0 text-[#DCE3E4]">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-[#DCE3E4] whitespace-nowrap">
                      {item.label}
                    </span>
                  </>
                ) : (
                  <div className="flex items-center justify-center w-14 h-14">
                    <div className="text-[#DCE3E4]">{item.icon}</div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Theme Toggle */}
          <div className={cn("mt-1", expanded ? "px-5" : "")}>
            <ThemeToggle expanded={expanded} />
          </div>

          {/* Logout Button */}
          <div className={cn("mt-1", expanded ? "px-5" : "")}>
            <button
              className={cn(
                "flex items-center gap-3 rounded-full transition-colors hover:bg-[#1A2425]",
                expanded
                  ? "px-4 py-4 w-full"
                  : "h-16 w-full flex-col justify-center"
              )}
              onClick={handleLogout}
            >
              {expanded ? (
                <>
                  <LogOut className="w-6 h-6 flex-shrink-0 text-[#DCE3E4]" />
                  <span className="text-sm font-medium text-[#DCE3E4] whitespace-nowrap">
                    Logout
                  </span>
                </>
              ) : (
                <div className="flex items-center justify-center w-14 h-14">
                  <LogOut className="w-6 h-6 text-[#DCE3E4]" />
                </div>
              )}
            </button>
          </div>

          {/* Toggle Button */}
          <div className={cn("mt-2", expanded ? "px-5" : "")}>
            <button
              className={cn(
                "flex items-center justify-center rounded-full transition-colors hover:bg-[#1A2425]",
                expanded ? "w-14 h-14" : "h-16 w-full"
              )}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <ChevronLeft className="w-6 h-6 text-[#DCE3E4]" />
              ) : (
                <ChevronRight className="w-6 h-6 text-[#DCE3E4]" />
              )}
            </button>
          </div>

          <div className="absolute bottom-1 w-full text-xs text-[#DCE3E430] text-center">
            v{packageJson.version}
          </div>
        </div>
      </nav>
    </aside>
  );
}
